import { Component, OnInit, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
// PrimeNG 21 Imports
import { MessageService, MenuItem } from 'primeng/api';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { Toast } from 'primeng/toast';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ColumnConfig, TABLE_CONFIG } from '../dashboard/table-config';
import { SubResponsibleRegistryService } from '../../services/sub-responsible-registry.service';
import { SubResponsible } from '../../models/sub-responsible.model';
import { RegistryTableComponent } from '../../../core/registry-table/registry-table.component';

@Component({
  selector: 'app-sub-responsibles-registry',
  standalone: true,
  providers: [DialogService, MessageService],
  imports: [
    CommonModule,
    DynamicDialogModule,
    Toast,
    RegistryTableComponent,
    BreadcrumbModule,
  ],
  templateUrl: './sub-responsibles-registry.component.html',
  styleUrl: './sub-responsibles-registry.component.scss',
})
export class SubResponsiblesRegistryComponent implements OnInit {
  private dialogService = inject(DialogService);
  private subResponsibleService = inject(SubResponsibleRegistryService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private titleService = inject(Title);

  // Table configuration
  tableTitle = 'Sub-Responsabili Registrati';
  tableIcon = 'pi-users';
  columns: ColumnConfig[] = [];
  data = signal<any[]>([]);
  loading = signal<boolean>(false);
  breadcrumbItems = signal<MenuItem[]>([{ label: 'Registro Sub-Responsabili' }]);

  ngOnInit(): void {
    this.titleService.setTitle('Registro Sub-Responsabili - PalEasy');
    this.loadConfiguration();
    this.loadManagers();
  }

  private loadConfiguration(): void {
    const config = TABLE_CONFIG['elencoSubResponsabili'];
    if (config) {
      this.columns = config.columns;
    } else {
      // Fallback configuration if not in TABLE_CONFIG
      this.columns = [
        { field: 'businessName', title: 'Ragione Sociale' },
        { field: 'headquarters', title: 'Sede' },
        { field: 'email', title: 'Email' },
        { field: 'createdAt', title: 'Data Creazione' }
      ];
    }
  }

  private loadManagers(): void {
    this.loading.set(true);
    this.subResponsibleService.getSubResponsibles().subscribe({
      next: (response) => {
        // Usa metodi centralizzati del servizio
        const dataArray = this.subResponsibleService.extractSubResponsiblesArray(response);
        const mappedData = this.subResponsibleService.mapSubResponsiblesForDisplay(dataArray);
        console.log('Sub-Responsibles Registry - Mapped data:', mappedData);
        this.data.set(mappedData);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading sub-responsibles:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore nel recupero dei dati',
          detail: 'Impossibile caricare l\'elenco dei sub-responsabili. Riprova più tardi.',
          life: 5000
        });
        this.data.set([]);
        this.loading.set(false);
      }
    });
  }

  // =====================
  // EVENT HANDLERS
  // =====================
  onDetailClick(rowData: any): void {
    if (rowData?.id) {
      this.router.navigate(['/axa/sub-responsible', rowData.id]);
    }
  }

  onDeleteClick(rowData: any): void {
    if (rowData?.id) {
      if (confirm(`Sei sicuro di voler eliminare il sub-responsabile "${rowData.businessName}"?`)) {
        this.subResponsibleService.deleteSubResponsible(rowData.id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Eliminazione completata',
              detail: 'Il sub-responsabile è stato eliminato con successo',
              life: 3000
            });
            this.loadManagers();
          },
          error: (error) => {
            console.error('Error deleting sub-responsible:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Impossibile eliminare il sub-responsabile. Riprova più tardi.',
              life: 5000
            });
          }
        });
      }
    }
  }

  onExport(): void {
    this.subResponsibleService.exportSubResponsiblesRegistry().subscribe({
      next: (response: any) => {
        const blob = response.body as Blob | null;
        if (!blob) {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore export',
            detail: 'File non ricevuto dal server',
            life: 4000
          });
          return;
        }
        let fileName = 'Registro Sub-Responsabili.xlsx';
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^";]+)"?/);
          if (match && match[1]) {
            fileName = decodeURIComponent(match[1]);
          }
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore export',
          detail: 'Impossibile esportare il registro sub-responsabili',
          life: 4000
        });
      }
    });
  }

  onClearFilters(): void {
    // Reset data to original
    this.loadManagers();
  }

  goToSubResponsibleWizard(): void {
    // TODO: Create sub-responsible registration wizard route
    this.messageService.add({
      severity: 'info',
      summary: 'Navigazione',
      detail: 'Wizard registrazione sub-responsabile in sviluppo',
      life: 3000
    });
  }
}
